/** Line set → Routes tab. The whole set is edited in ONE draft held in local
 *  state (set fields + routes + each route's ordered stop-links + breaks); a
 *  single Save rebuilds the link-wrapped payload and PATCHes it.
 *
 *  Structure:
 *   - Set fields: name, atag (access tags), tags (per-TO tags).
 *   - Routes as TABS: add / remove / reorder (↑↓). Per-route: name, color,
 *     enabled, start/end time (HH:MM:SS), loop_duration shown in MINUTES ↔ sent
 *     as SECONDS, frequency, warning, region + country pickers, breaks[] rows.
 *   - Ordered stop-links: a RemoteAutocomplete over `/route_stops/` adds an
 *     existing stop; ↑↓ reorder; per-link loop_offset + first/last-departure.
 *
 *  Dirty-guard: navigating away with unsaved changes prompts (react-router
 *  `useBlocker` via `useUnsavedChangesPrompt`). On a 409 the ConflictBanner
 *  reloads the whole set.
 *
 *  Deferred: embedded map, live vehicle positions, KML, inline stop creation,
 *  drag-drop (↑↓ only). */
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box, Button, Checkbox, Divider, FormControlLabel, IconButton, Stack, Tab, Tabs,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import * as regionsApi from '@/api/regions';
import type { Country, RegionListItem } from '@/api/regions';
import type {
  RouteSetDetail, RouteSetWriteJson, RouteStopLink,
} from '@/api/routeSets';
import * as routeStopsApi from '@/api/routeStops';
import type { RouteStopListItem } from '@/api/routeStops';
import * as toursApi from '@/api/tours';
import type { NamedRef, PickerOption } from '@/api/tours';
import { ConflictBanner, EditorSaveBar } from '@/components/EditorSaveBar/EditorSaveBar';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { PickerLinks } from '@/components/ValueActions/PickerLinks';
import { act, ValueActions } from '@/components/ValueActions/ValueActions';
import { usePermissions } from '@/hooks/usePermissions';

import {
  busStopActions,
  catalogTagActions,
  regionActions,
  type CatalogCtx,
} from '../../catalog/catalogLinks';
import { useRouteSetPatch } from '../hooks/useRouteSetPatch';

// ---------------------------------------------------------------------------
// Draft model (mirrors the write shape but keeps display refs + minutes)
// ---------------------------------------------------------------------------

interface StopLinkDraft {
  stopId: number;
  stopName: string;
  /** Carried through from the read shape so a stop row can offer the map;
   *  a stop added by the picker has none until the set is reloaded. */
  stopLat: number | null;
  stopLng: number | null;
  loop_offset: number;
  first_departure: string;
  last_full_loop: string;
  last_departure: string;
}

interface BreakDraft {
  id?: number;
  start_time: string;
  end_time: string;
}

interface RouteDraft {
  /** Existing route id (undefined for a new route). */
  id?: number;
  name: string;
  enabled: boolean;
  color: string;
  start_time: string;
  end_time: string;
  /** Shown to the user in MINUTES; '' = null. Sent as seconds. */
  loop_minutes: string;
  frequency: string;
  warning: string;
  region: NamedRef | null;
  country: Country | null;
  tags: PickerOption[];
  breaks: BreakDraft[];
  stops: StopLinkDraft[];
}

interface SetDraft {
  name: string;
  atag: PickerOption[];
  tags: PickerOption[];
  routes: RouteDraft[];
}

// ---- seed (record → draft) ------------------------------------------------

const secToMin = (s: number | null): string => (s == null ? '' : String(Math.round(s / 60)));
const minToSec = (m: string): number | null => {
  if (m.trim() === '') return null;
  const n = Number(m);
  return Number.isNaN(n) ? null : Math.round(n * 60);
};
const blankToNull = (s: string): string | null => (s.trim() === '' ? null : s.trim());

function seedTags(xs: NamedRef[]): PickerOption[] {
  return xs.map((t) => ({ id: t.id, name: t.name }));
}

function seedStopLink(l: RouteStopLink): StopLinkDraft {
  return {
    stopId: l.stop.id,
    stopName: l.stop.name,
    stopLat: l.stop.latitude,
    stopLng: l.stop.longitude,
    loop_offset: l.loop_offset ?? 0,
    first_departure: l.first_departure ?? '',
    last_full_loop: l.last_full_loop ?? '',
    last_departure: l.last_departure ?? '',
  };
}

function seedDraft(set: RouteSetDetail): SetDraft {
  const routes = [...set.routes]
    .sort((a, b) => a.sequence - b.sequence)
    .map((link): RouteDraft => {
      const r = link.route;
      return {
        id: r.id,
        name: r.name,
        enabled: r.enabled,
        color: r.color ?? '',
        start_time: r.start_time ?? '',
        end_time: r.end_time ?? '',
        loop_minutes: secToMin(r.loop_duration),
        frequency: r.frequency ?? '',
        warning: r.warning ?? '',
        region: r.region ?? null,
        country: r.country ? { id: r.country.id, name: r.country.name ?? '', country_code: null } : null,
        tags: seedTags(r.tags),
        breaks: [...r.breaks].map((b) => ({ id: b.id, start_time: b.start_time ?? '', end_time: b.end_time ?? '' })),
        stops: [...r.stops].sort((a, b) => a.sequence - b.sequence).map(seedStopLink),
      };
    });
  return {
    name: set.name,
    atag: seedTags(set.atag),
    tags: seedTags(set.tags),
    routes,
  };
}

// ---- build (draft → write payload) ----------------------------------------

function buildBody(draft: SetDraft): RouteSetWriteJson {
  return {
    name: draft.name.trim(),
    atag: draft.atag.map((t) => ({ id: t.id })),
    tags: draft.tags.map((t) => ({ id: t.id })),
    routes: draft.routes.map((r, ri) => ({
      sequence: ri,
      route: {
        ...(r.id != null ? { id: r.id } : {}),
        name: r.name.trim(),
        enabled: r.enabled,
        color: r.color.trim(),
        start_time: blankToNull(r.start_time),
        end_time: blankToNull(r.end_time),
        loop_duration: minToSec(r.loop_minutes),
        frequency: blankToNull(r.frequency),
        warning: blankToNull(r.warning),
        region_id: r.region?.id ?? null,
        country_id: r.country?.id ?? null,
        tags: r.tags.map((t) => ({ id: t.id })),
        breaks: r.breaks.map((b) => ({
          ...(b.id != null ? { id: b.id } : {}),
          start_time: blankToNull(b.start_time),
          end_time: blankToNull(b.end_time),
        })),
        stops: r.stops.map((s, si) => ({
          sequence: si,
          loop_offset: s.loop_offset,
          first_departure: blankToNull(s.first_departure),
          last_full_loop: blankToNull(s.last_full_loop),
          last_departure: blankToNull(s.last_departure),
          stop: { id: s.stopId },
        })),
      },
    })),
  };
}

/** Immutable array move (swap i with i+d), no-op at the edges. */
function move<T>(arr: T[], i: number, d: -1 | 1): T[] {
  const j = i + d;
  if (j < 0 || j >= arr.length) return arr;
  const copy = [...arr];
  [copy[i], copy[j]] = [copy[j]!, copy[i]!];
  return copy;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LineSetRoutesTab({ set, onSaved }: { set: RouteSetDetail; onSaved: () => void }) {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_fleet_manage');
  const patch = useRouteSetPatch(set, onSaved);

  const [draft, setDraft] = useState<SetDraft>(() => seedDraft(set));
  const [activeRoute, setActiveRoute] = useState(0);

  useEffect(() => {
    setDraft(seedDraft(set));
    setActiveRoute(0);
    // Re-seed on any server change (incl. after save→refetch, so the editor
    // isn't stuck dirty). Keyed on the whole record object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [set]);

  // Dirty = the rebuilt payload differs from the record's rebuilt payload.
  const dirty = useMemo(
    () => JSON.stringify(buildBody(draft)) !== JSON.stringify(buildBody(seedDraft(set))),
    [draft, set],
  );

  // ---- set-level mutators ---------------------------------------------------
  const patchSet = (p: Partial<SetDraft>) => setDraft((d) => ({ ...d, ...p }));

  // ---- route-level mutators -------------------------------------------------
  const patchRoute = (ri: number, p: Partial<RouteDraft>) =>
    setDraft((d) => ({ ...d, routes: d.routes.map((r, i) => (i === ri ? { ...r, ...p } : r)) }));

  const addRoute = () =>
    setDraft((d) => {
      const next: RouteDraft = {
        name: `Route ${d.routes.length + 1}`,
        enabled: true,
        color: '#00aaff',
        start_time: '',
        end_time: '',
        loop_minutes: '',
        frequency: '',
        warning: '',
        region: null,
        country: null,
        tags: [],
        breaks: [],
        stops: [],
      };
      return { ...d, routes: [...d.routes, next] };
    });

  const removeRoute = (ri: number) =>
    setDraft((d) => ({ ...d, routes: d.routes.filter((_, i) => i !== ri) }));

  const moveRoute = (ri: number, dir: -1 | 1) =>
    setDraft((d) => ({ ...d, routes: move(d.routes, ri, dir) }));

  const route = draft.routes[activeRoute];
  const ctx: CatalogCtx = { tenantId: set.tenant.id };

  const submit = () => patch.save(buildBody(draft));

  return (
    <Box>
      <ConflictBanner
        open={patch.conflict}
        onReload={onSaved}
        message="This line set changed on the server (another editor). Reload before saving."
      />

      {/* --- Set fields --- */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>Line set</Typography>
        <Stack spacing={1.5}>
          <TextField
            label="Name" size="small" fullWidth value={draft.name} disabled={!canManage}
            onChange={(e) => patchSet({ name: e.target.value })}
          />
          <RemoteAutocomplete<PickerOption>
            label="Access tags" size="small" multiple fetchOnEmpty value={draft.atag}
            getOptionLabel={(o) => o.name ?? `#${o.id}`} getOptionKey={(o) => o.id}
            queryFn={async (q) => (await toursApi.picker(set.tenant.id, 'atags', q)).results}
            onChange={(v) => patchSet({ atag: (v as PickerOption[]) ?? [] })}
          />
          <RemoteAutocomplete<PickerOption>
            label="Tags" size="small" multiple fetchOnEmpty value={draft.tags}
            getOptionLabel={(o) => o.name ?? `#${o.id}`} getOptionKey={(o) => o.id}
            queryFn={async (q) => (await toursApi.picker(set.tenant.id, 'tags', q)).results}
            onChange={(v) => patchSet({ tags: (v as PickerOption[]) ?? [] })}
          />
          {/* The picker keeps its chips; each tag's links live underneath.
              Access tags have no console route, so they stay plain. */}
          <PickerLinks
            items={draft.tags.map((t) => ({
              key: t.id,
              label: t.name ?? `#${t.id}`,
              actions: catalogTagActions(t, ctx),
            }))}
          />
        </Stack>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* --- Route tabs --- */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Tabs
          value={draft.routes.length ? Math.min(activeRoute, draft.routes.length - 1) : false}
          onChange={(_, v) => setActiveRoute(v as number)}
          variant="scrollable" scrollButtons="auto" sx={{ flex: 1, minHeight: 40 }}
        >
          {draft.routes.map((r, i) => (
            <Tab key={r.id ?? `new-${i}`} value={i} label={r.name || `Route ${i + 1}`} sx={{ minHeight: 40 }} />
          ))}
        </Tabs>
        {canManage && (
          <Button size="small" startIcon={<AddIcon />} onClick={addRoute}>Add route</Button>
        )}
      </Stack>

      {draft.routes.length === 0 || !route ? (
        <Typography variant="body2" sx={{ color: 'text.disabled', py: 3 }}>
          No routes. {canManage ? 'Add a route to begin.' : ''}
        </Typography>
      ) : (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
          {/* route reorder / remove controls */}
          {canManage && (
            <Stack direction="row" justifyContent="flex-end" spacing={0.5} sx={{ mb: 1 }}>
              <IconButton size="small" onClick={() => moveRoute(activeRoute, -1)} disabled={activeRoute === 0}
                aria-label="Move route left"><ArrowUpwardIcon fontSize="small" sx={{ transform: 'rotate(-90deg)' }} /></IconButton>
              <IconButton size="small" onClick={() => moveRoute(activeRoute, 1)} disabled={activeRoute >= draft.routes.length - 1}
                aria-label="Move route right"><ArrowDownwardIcon fontSize="small" sx={{ transform: 'rotate(-90deg)' }} /></IconButton>
              <IconButton size="small" color="error" onClick={() => { removeRoute(activeRoute); setActiveRoute((a) => Math.max(0, a - 1)); }}
                aria-label="Remove route"><DeleteIcon fontSize="small" /></IconButton>
            </Stack>
          )}

          {/* route settings */}
          <Stack spacing={1.5}>
            {/* A route has no page of its own — its id is all there is to
                offer, and it is the handle every backend log names it by. */}
            {route.id != null && (
              <Typography variant="caption" color="text.secondary" component="div">
                <ValueActions actions={[act.copy(String(route.id), 'Copy route id')]}>
                  Route ID {route.id}
                </ValueActions>
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField label="Route name" size="small" fullWidth value={route.name} disabled={!canManage}
                onChange={(e) => patchRoute(activeRoute, { name: e.target.value })} />
              <TextField label="Color" size="small" type="color" sx={{ width: 90 }} value={route.color || '#00aaff'} disabled={!canManage}
                onChange={(e) => patchRoute(activeRoute, { color: e.target.value })} />
              <FormControlLabel sx={{ whiteSpace: 'nowrap' }}
                control={<Checkbox size="small" checked={route.enabled} disabled={!canManage}
                  onChange={(e) => patchRoute(activeRoute, { enabled: e.target.checked })} />}
                label="Enabled" />
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <TextField label="Start time" size="small" placeholder="HH:MM:SS" sx={{ width: 140 }} value={route.start_time} disabled={!canManage}
                onChange={(e) => patchRoute(activeRoute, { start_time: e.target.value })} />
              <TextField label="End time" size="small" placeholder="HH:MM:SS" sx={{ width: 140 }} value={route.end_time} disabled={!canManage}
                onChange={(e) => patchRoute(activeRoute, { end_time: e.target.value })} />
              <TextField label="Loop duration (min)" size="small" type="number" sx={{ width: 170 }} value={route.loop_minutes} disabled={!canManage}
                onChange={(e) => patchRoute(activeRoute, { loop_minutes: e.target.value })} />
              <TextField label="Frequency" size="small" sx={{ width: 160 }} value={route.frequency} disabled={!canManage}
                onChange={(e) => patchRoute(activeRoute, { frequency: e.target.value })} />
            </Box>

            <TextField label="Warning" size="small" fullWidth multiline minRows={2} value={route.warning} disabled={!canManage}
              onChange={(e) => patchRoute(activeRoute, { warning: e.target.value })} />

            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Box sx={{ flex: 1 }}>
                <RemoteAutocomplete<RegionListItem>
                  label="Region" size="small" fetchOnEmpty value={route.region as RegionListItem | null}
                  getOptionLabel={(o) => o.name} getOptionKey={(o) => o.id}
                  queryFn={async (q) => (await regionsApi.list({ tour_operator_id: set.tenant.id, search: q || undefined, pageSize: 50 })).results}
                  onChange={(v) => {
                    const r = v as RegionListItem | null;
                    patchRoute(activeRoute, { region: r ? { id: r.id, name: r.name } : null });
                  }}
                />
                <PickerLinks
                  items={
                    route.region
                      ? [{
                          key: route.region.id,
                          label: route.region.name ?? `#${route.region.id}`,
                          actions: regionActions(route.region, ctx),
                        }]
                      : []
                  }
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <RemoteAutocomplete<Country>
                  label="Country" size="small" fetchOnEmpty value={route.country}
                  getOptionLabel={(o) => o.name} getOptionKey={(o) => o.id}
                  queryFn={async (q) => (await regionsApi.listCountries(q)).results}
                  onChange={(v) => patchRoute(activeRoute, { country: (v as Country | null) ?? null })}
                />
              </Box>
            </Box>

            {/* breaks */}
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Breaks</Typography>
              {route.breaks.map((b, bi) => (
                <Stack key={b.id ?? `new-${bi}`} direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                  <TextField label="Start" size="small" placeholder="HH:MM:SS" sx={{ width: 130 }} value={b.start_time} disabled={!canManage}
                    onChange={(e) => patchRoute(activeRoute, { breaks: route.breaks.map((x, j) => (j === bi ? { ...x, start_time: e.target.value } : x)) })} />
                  <TextField label="End" size="small" placeholder="HH:MM:SS" sx={{ width: 130 }} value={b.end_time} disabled={!canManage}
                    onChange={(e) => patchRoute(activeRoute, { breaks: route.breaks.map((x, j) => (j === bi ? { ...x, end_time: e.target.value } : x)) })} />
                  {canManage && (
                    <IconButton size="small" onClick={() => patchRoute(activeRoute, { breaks: route.breaks.filter((_, j) => j !== bi) })}
                      aria-label="Remove break"><DeleteIcon fontSize="small" /></IconButton>
                  )}
                </Stack>
              ))}
              {canManage && (
                <Button size="small" startIcon={<AddIcon />} sx={{ mt: 0.5 }}
                  onClick={() => patchRoute(activeRoute, { breaks: [...route.breaks, { start_time: '', end_time: '' }] })}>
                  Add break
                </Button>
              )}
            </Box>

            <Divider />

            {/* stop-links */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>Stops on this route</Typography>
              {route.stops.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.disabled', mb: 1 }}>No stops.</Typography>
              ) : (
                <Table size="small" sx={{ mb: 1 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Stop</TableCell>
                      <TableCell sx={{ width: 110 }}>Loop offset</TableCell>
                      <TableCell sx={{ width: 130 }}>First dep.</TableCell>
                      <TableCell sx={{ width: 130 }}>Last full loop</TableCell>
                      <TableCell sx={{ width: 130 }}>Last dep.</TableCell>
                      {canManage && <TableCell align="right" sx={{ width: 120 }}>Order</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {route.stops.map((s, si) => (
                      <TableRow key={`${s.stopId}-${si}`}>
                        <TableCell>
                          <ValueActions
                            actions={busStopActions({ id: s.stopId, latitude: s.stopLat, longitude: s.stopLng })}
                          >
                            {s.stopName || `#${s.stopId}`}
                          </ValueActions>
                        </TableCell>
                        <TableCell>
                          <TextField type="number" size="small" variant="standard" value={s.loop_offset} disabled={!canManage}
                            onChange={(e) => patchRoute(activeRoute, { stops: route.stops.map((x, j) => (j === si ? { ...x, loop_offset: Number(e.target.value) || 0 } : x)) })} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" variant="standard" placeholder="HH:MM:SS" value={s.first_departure} disabled={!canManage}
                            onChange={(e) => patchRoute(activeRoute, { stops: route.stops.map((x, j) => (j === si ? { ...x, first_departure: e.target.value } : x)) })} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" variant="standard" placeholder="HH:MM:SS" value={s.last_full_loop} disabled={!canManage}
                            onChange={(e) => patchRoute(activeRoute, { stops: route.stops.map((x, j) => (j === si ? { ...x, last_full_loop: e.target.value } : x)) })} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" variant="standard" placeholder="HH:MM:SS" value={s.last_departure} disabled={!canManage}
                            onChange={(e) => patchRoute(activeRoute, { stops: route.stops.map((x, j) => (j === si ? { ...x, last_departure: e.target.value } : x)) })} />
                        </TableCell>
                        {canManage && (
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => patchRoute(activeRoute, { stops: move(route.stops, si, -1) })} disabled={si === 0}
                              aria-label="Move stop up"><ArrowUpwardIcon fontSize="small" /></IconButton>
                            <IconButton size="small" onClick={() => patchRoute(activeRoute, { stops: move(route.stops, si, 1) })} disabled={si === route.stops.length - 1}
                              aria-label="Move stop down"><ArrowDownwardIcon fontSize="small" /></IconButton>
                            <IconButton size="small" onClick={() => patchRoute(activeRoute, { stops: route.stops.filter((_, j) => j !== si) })}
                              aria-label="Remove stop"><DeleteIcon fontSize="small" /></IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {canManage && (
                <Box sx={{ maxWidth: 420 }}>
                  <RemoteAutocomplete<RouteStopListItem>
                    label="Add stop" size="small" fetchOnEmpty value={null}
                    getOptionLabel={(o) => o.name} getOptionKey={(o) => o.id}
                    queryFn={async (q) => {
                      const r = await routeStopsApi.list({ tour_operator_id: set.tenant.id, search: q || undefined, pageSize: 50 });
                      return r.results;
                    }}
                    onChange={(v) => {
                      const opt = v as RouteStopListItem | null;
                      if (!opt) return;
                      patchRoute(activeRoute, {
                        stops: [...route.stops, {
                          stopId: opt.id, stopName: opt.name, stopLat: null, stopLng: null,
                          loop_offset: 0,
                          first_departure: '', last_full_loop: '', last_departure: '',
                        }],
                      });
                    }}
                  />
                </Box>
              )}
            </Box>
          </Stack>
        </Box>
      )}

      <EditorSaveBar
        dirty={dirty}
        saving={patch.saving}
        canManage={canManage}
        onSave={submit}
        onDiscard={() => setDraft(seedDraft(set))}
        guardMessage="You have unsaved changes on this line set. Leaving now will discard them."
      />
    </Box>
  );
}
